// ANGEL FLIGHT — thermal AC-130 render tunables (single source of truth).
//
// The look IS the post-processing. Every knob for the FLIR gun-camera lives
// here: camera framing, heat authoring, and the post chain (LUT contrast/clip,
// bloom, grain, scanlines, vignette, chromatic aberration, fog, gimbal drift).
// No magic numbers in the render layer — pull from here.
//
// World space (from balance.ts): x lateral metres (±halfWidth), z depth metres
// (nearZ..farZ), y height metres (ground = 0). The camera sits HIGH and looks
// DOWN at that ground band — the AC-130 sensor footprint.

export const THERMAL = Object.freeze({
  /** The gunship sensor camera (looks down at the ground band). */
  camera: {
    /** Altitude of the sensor above the ground plane (metres). High = top-down. */
    altitudeM: 16,
    /** How far back (toward the near edge, +z in world) the camera sits (metres).
     *  Small relative to altitude → steep downward oblique. */
    setbackM: 7,
    /** Depth (world z, metres) the camera aims at — centre of the kill box. */
    lookAtZ: 13,
    /** Vertical field of view (deg). Longish lens flattens perspective like a
     *  real sensor pod and keeps blobs readable. */
    fovDeg: 34,
    nearM: 0.5,
    farM: 80,
  },

  /** Heat authoring: luminance == temperature by construction. Emissive values
   *  drive the LUT; hot things exceed 1.0 so they blow the sensor to white. */
  heat: {
    ground: 0.06, // cold asphalt — falls to black under fog
    rubble: 0.1,
    enemyBody: 0.62, // warm contact
    enemyCore: 0.82, // torso/head run hotter
    enemyBrute: 0.7, // bigger, slightly hotter mass
    enemyRunner: 0.5, // leaner = dimmer
    muzzle: 2.6, // blows to white
    tracer: 2.2,
    impact: 2.4,
    explosion: 3.2,
    bloodCool: 0.0, // spilled = cold (dark) on the ground
  },

  /** Thermal LUT + sensor response. */
  flir: {
    /** Contrast curve exponent on luminance before the ramp (>1 deepens darks,
     *  <1 lifts them). 0.85 since the FLIR mode became a POST over the lit
     *  COLOUR world (G1): the dusk frame's luminance is far darker than the old
     *  authored-emissive thermal world, so the old 1.35 crushed the ground to
     *  black — this lifts it to a readable sensor grey while the clip still
     *  blows the hot tail to white. */
    contrast: 0.85,
    /** Luminance at/above which the sensor saturates to pure white (#1 FLIR tell). */
    clipThreshold: 0.92,
    /** Default polarity: false = white-hot (hot = bright), true = black-hot. */
    blackHot: false,
    /** Palette: 'whiteHot' (grayscale) or 'ironbow' (blue→yellow→red→white). */
    palette: 'whiteHot' as 'whiteHot' | 'ironbow',
  },

  /** Bloom on the clipped highlights → thermal blooming halos. Stronger + wider
   *  so hot contacts and muzzle/explosions genuinely glow + bleed past edges. */
  bloom: {
    intensity: 1.5,
    luminanceThreshold: 0.5,
    luminanceSmoothing: 0.3,
    radius: 0.85,
    /** Render bloom at half-res on mobile for fill-rate. */
    mipmapBlur: true,
  },

  /** Sensor artefacts (merged into one EffectPass). Calmer grain so it reads as
   *  a clean modern FLIR feed, not video static. */
  sensor: {
    grain: 0.08, // animated noise strength
    fixedPattern: 0.035, // faint vertical streak (uncooled microbolometer tell)
    scanline: 0.05, // horizontal interlace strength
    scanlineCount: 720, // one per logical row
    vignette: 0.55, // radial edge darkening
    chromaticAberration: 0.0014, // grows toward frame edges
  },

  /** Distance fog so cold ground recedes into black. */
  fog: {
    color: 0x000000,
    density: 0.022,
  },

  /** Gimbal float — slow camera drift + reticle micro-jitter (sells "moving
   *  aircraft" + "stabilised-but-floating sensor"). */
  gimbal: {
    driftAmpDeg: 0.5, // peak yaw/pitch wander
    driftRateHz: 0.05, // very slow
    reticleJitterPx: 1.5,
  },
} as const);
